import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getCloudinaryThumbnail } from '../lib/cloudinaryUtils';

export const CartDrawer = () => {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity,
    subtotal,
    itemCount,
    getWhatsAppUrl,
    clearCart
  } = useCart();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeCart}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            data-testid="cart-overlay"
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-[420px] bg-[#0a0a0a] z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-white">Carrito</h2>
                {items.length > 0 && (
                  <span className="bg-white/10 text-white/60 text-xs px-2 py-0.5 rounded-full">
                    {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>
              <button
                onClick={closeCart}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-white/50 hover:bg-white/10 hover:text-white transition-all"
                data-testid="close-cart-button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full px-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-white/[0.03] flex items-center justify-center mb-5">
                    <ShoppingBag className="w-8 h-8 text-white/15" />
                  </div>
                  <p className="text-white/50 text-base mb-1">Tu carrito está vacío</p>
                  <p className="text-white/20 text-sm mb-8">Descubrí nuestras colecciones</p>
                  <button
                    onClick={closeCart}
                    className="flex items-center gap-2 text-gold text-sm font-medium hover:text-gold-hover transition-colors"
                    data-testid="continue-shopping-button"
                  >
                    Explorar tienda
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="px-5">
                  <AnimatePresence initial={false}>
                    {items.map((item, index) => (
                      <motion.div
                        key={item.id}
                        layout
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: 80, transition: { duration: 0.2 } }}
                        transition={{ delay: index * 0.03 }}
                        className="flex gap-3 py-4 border-b border-white/[0.04] last:border-0"
                        data-testid={`cart-item-${item.id}`}
                      >
                        {/* Image */}
                        <div className="w-[72px] h-[90px] rounded-xl overflow-hidden bg-[#111] flex-shrink-0">
                          {item.image ? (
                            <img
                              src={getCloudinaryThumbnail(item.image)}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white/10">
                              <ShoppingBag className="w-6 h-6" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                          <div>
                            <h3 className="text-white text-sm font-medium truncate pr-6">
                              {item.name}
                            </h3>
                            <p className="text-white/30 text-xs mt-0.5">
                              {item.size} · {item.color}
                            </p>
                          </div>

                          <div className="flex items-center justify-between">
                            {/* Quantity */}
                            <div className="flex items-center bg-white/[0.04] rounded-full h-7">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white transition-colors"
                                data-testid={`decrease-quantity-${item.id}`}
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="text-white text-xs font-medium min-w-[20px] text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                disabled={item.quantity >= item.maxStock}
                                className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white transition-colors disabled:opacity-20"
                                data-testid={`increase-quantity-${item.id}`}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>

                            {/* Price */}
                            <span className="text-white text-sm font-medium">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        </div>

                        {/* Remove */}
                        <button
                          onClick={() => removeItem(item.id)}
                          className="absolute right-5 mt-0.5 text-white/15 hover:text-red-400 transition-colors"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Clear all */}
                  {items.length > 1 && (
                    <button
                      onClick={clearCart}
                      className="w-full py-3 text-white/15 hover:text-red-400/60 text-xs transition-colors text-center"
                      data-testid="clear-cart-button"
                    >
                      Vaciar carrito
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-white/[0.06] bg-[#0a0a0a]">
                {/* Subtotal */}
                <div className="px-5 py-4">
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="text-white/40 text-sm">Subtotal</span>
                    <span className="text-white text-xl font-semibold">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-white/15 text-xs">Envío calculado al finalizar</p>
                </div>

                {/* Actions */}
                <div className="px-5 pb-5 space-y-2.5">
                  {/* WhatsApp checkout - primary */}
                  <motion.a
                    whileTap={{ scale: 0.98 }}
                    href={getWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full h-12 bg-[#25D366] hover:bg-[#20BA5C] text-white rounded-full font-medium text-sm transition-colors"
                    data-testid="whatsapp-checkout-button"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Finalizar pedido
                  </motion.a>

                  {/* Continue shopping */}
                  <button
                    onClick={closeCart}
                    className="w-full h-10 text-white/30 hover:text-white/60 text-sm transition-colors"
                    data-testid="continue-shopping-link"
                  >
                    Seguir comprando
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
